import {
  Chip,
  Divider,
  Grid,
  makeStyles,
  Paper,
  Typography,
} from "@material-ui/core";
import {
  formatUtcDate,
  standardDateFormats,
} from "@ofqual/portal-core/core/utils/dateUtils";
import React from "react";
import { NonNullableItemState } from "../redux/state";
import { NewsAppBar } from "./NewsAppBar";
import { AttachmentList } from "./common/AttachmentList";
import { useSelector } from "react-redux";
import { authSelector } from "../redux/selectors";
import { newsService, tagsService } from "../services";
import { useLookup } from "@ofqual/portal-core/core/hooks";
import { getLogo } from "./logoHelper";

const useStyles = makeStyles((theme) => ({
  formSection: {
    padding: 30,
    boxShadow: "0 0 19px -2px hsla(0,0%,63.9%,.3)",
    border: "1px solid #ddd",
    backgroundColor: "#fff",
    borderRadius: "3px",
  },
  logoContainer: {
    height: 40,
    float: "right",
  },
  divider: { margin: "15px 0px" },
  tags: { display: "block", marginTop: 10 },
}));

export const NewsItemDetails: React.FC<{ state: NonNullableItemState }> = ({
  state,
}) => {
  const classes = useStyles();
  const item = state.item;
  const { token } = useSelector(authSelector);

  const [tagsLookups] = useLookup({
    service: tagsService,
    id: "id",
    label: "tag",
  });

  const buildGetBlob = (blobId: string): Promise<Blob> =>
    newsService.downloadAttachment(item.id, blobId, { token });
  return tagsLookups && tagsLookups.length ? (
    <div>
      <NewsAppBar breadcrumbs={[{ label: `${item.title}` }]} />
      <Paper className={classes.formSection}>
        {item.archived ? (
          <Typography variant="h5" align="left">
            This article has been archived
          </Typography>
        ) : (
          <>
            <Grid container spacing={3}>
              <Grid item md={8}>
                <Typography variant="h5" align="left">
                  {item.title}
                </Typography>
                <Typography variant="subtitle1" align="left">
                  {item.subHeading}
                </Typography>
                <b>Published on</b>{" "}
                {formatUtcDate(
                  item.publishedDate,
                  standardDateFormats.FULL_DATE_TIME
                )}{" "}
                <b>Published by</b> {item.createdByDisplayName}
                {item.resourceTags && item.resourceTags.length ? (
                  <>
                    <div className={classes.tags}>
                      {item.resourceTags.map((item) => (
                        <Chip
                          size="small"
                          style={{ margin: 2, display: "flex-grid" }}
                          label={
                            tagsLookups.find((x) => x.id === item)?.label ||
                            item
                          }
                          key={item}
                        />
                      ))}
                    </div>
                  </>
                ) : null}
              </Grid>

              <Grid item md={4}>
                <div className={classes.logoContainer}>
                  {getLogo(item.type)}
                </div>
              </Grid>
            </Grid>
            <Divider className={classes.divider} />
            <Grid container spacing={3}>
              {item.imageSrc ? (
                <Grid item md={8}>
                  <img
                    src={item.imageSrc}
                    alt="news"
                    style={{ maxWidth: "75%" }}
                  ></img>
                </Grid>
              ) : null}

              {item.videoEmbed ? (
                <Grid item xs={8}>
                  {/* TODO: find a better way to do this, dangerouslySetInnerHTML is bad! */}
                  <div dangerouslySetInnerHTML={{ __html: item.videoEmbed }} />
                </Grid>
              ) : null}

              {item.content ? (
                <Grid item md={12}>
                  <Typography
                    className="form-field"
                    variant="body1"
                    align="left"
                    dangerouslySetInnerHTML={{ __html: item.content }}
                    id="news-item-body"
                  ></Typography>
                </Grid>
              ) : null}

              {item.newsItemAttachments && item.newsItemAttachments.length ? (
                <>
                  <b>Attachments: </b>
                  <AttachmentList
                    canEdit={false}
                    attachments={item.newsItemAttachments}
                    getBlob={buildGetBlob}
                  />
                </>
              ) : null}
            </Grid>
          </>
        )}
      </Paper>
    </div>
  ) : null;
};
